import React from 'react';
import { Box, IconButton } from '@mui/material';
import { Delete } from '@mui/icons-material';
import { useDrag } from 'react-dnd';
import { SpanningGridWidgetProps, DragItem } from '../types';
import { ITEM_TYPE } from '../constants';
import { WidgetDisplay } from './WidgetDisplay';

export const SpanningGridWidget: React.FC<SpanningGridWidgetProps> = ({ 
    item, 
    colStart, 
    colSpan, 
    rowStart, 
    rowSpan, 
    onWidgetClick, 
    onWidgetDelete 
}) => {
    const [{ isDragging }, drag] = useDrag(() => ({
        type: ITEM_TYPE,
        item: { type: ITEM_TYPE, existingItem: item, isNew: false } as DragItem,
        collect: (monitor) => ({
            isDragging: monitor.isDragging()
        })
    }), [item]);

    return (
        <Box
            ref={drag}
            sx={{
                gridColumn: `${colStart} / span ${colSpan}`,
                gridRow: `${rowStart} / span ${rowSpan}`,
                border: '2px solid',
                borderColor: 'primary.main',
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                borderRadius: 2,
                minHeight: '120px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                cursor: isDragging ? 'grabbing' : 'grab',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                transition: 'all 0.2s ease-in-out',
                opacity: isDragging ? 0.5 : 1,
                '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                    transform: isDragging ? 'none' : 'translateY(-1px)',
                    boxShadow: '0 6px 12px rgba(0,0,0,0.2)',
                    '& .widget-content': {
                        transform: isDragging ? 'none' : 'scale(1.02)'
                    }
                }
            }}
            onClick={() => !isDragging && onWidgetClick(item)}
        >
            <Box 
                className="widget-content" 
                sx={{ 
                    width: '100%', 
                    height: '100%',
                    transition: 'transform 0.2s ease-in-out'
                }}
            >
                <WidgetDisplay 
                    item={item} 
                    showSpanInfo={true}
                    colSpan={colSpan}
                    rowSpan={rowSpan}
                />
            </Box>
            <IconButton
                size="small"
                sx={{ 
                    position: 'absolute', 
                    top: 8, 
                    right: 8,
                    color: 'white',
                    backgroundColor: 'rgba(244, 67, 54, 0.9)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    '&:hover': {
                        backgroundColor: '#f44336',
                        transform: 'scale(1.1)'
                    }
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    onWidgetDelete(item);
                }}
            >
                <Delete fontSize="small" />
            </IconButton>
        </Box>
    );
};